import React, { useState } from "react";
import {
  Lock,
  Eye,
  EyeOff,
  Check,
  X,
  Shield,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { supabase } from "../config/supabase";

interface ResetSenhaPrimeiroAcessoProps {
  usuario: {
    id: string;
    email: string;
    nome_completo: string;
  };
  onSuccess: () => void;
}

const ResetSenhaPrimeiroAcesso: React.FC<ResetSenhaPrimeiroAcessoProps> = ({
  usuario,
  onSuccess,
}) => {
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Validações da senha
  const validacoes = {
    minimo8: novaSenha.length >= 8,
    temMaiuscula: /[A-Z]/.test(novaSenha),
    temMinuscula: /[a-z]/.test(novaSenha),
    temNumero: /\d/.test(novaSenha),
    temEspecial: /[!@#$%^&*(),.?":{}|<>]/.test(novaSenha),
    senhasIguais: novaSenha === confirmarSenha && novaSenha.length > 0,
  };

  const senhaValida = Object.values(validacoes).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!senhaValida) {
      setErro("Por favor, atenda a todos os requisitos de senha");
      return;
    }

    setLoading(true);
    setErro(null);

    try {
      // Chamar função do banco para resetar senha
      const { data, error } = await supabase.rpc(
        "resetar_senha_primeiro_acesso",
        {
          usuario_id: usuario.id,
          nova_senha: novaSenha,
        },
      );

      if (error) {
        throw error;
      }

      // Atualizar a sessão do usuário com a nova senha
      const { error: updateError } = await supabase.auth.updateUser({
        password: novaSenha,
      });

      if (updateError) {
        throw updateError;
      }

      onSuccess();
    } catch (err) {
      console.error("Erro ao resetar senha:", err);
      setErro(err instanceof Error ? err.message : "Erro ao atualizar senha");
    } finally {
      setLoading(false);
    }
  };

  const ValidacaoItem = ({
    valida,
    texto,
  }: {
    valida: boolean;
    texto: string;
  }) => (
    <div
      className={`flex items-center gap-2 text-sm ${valida ? "text-green-600" : "text-gray-500"}`}
    >
      {valida ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
      <span>{texto}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/logo/logoEhsFacil.png"
              alt="CIPA FÁCIL EHS Logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Bem-vindo ao CIPA FÁCIL EHS!
          </h1>
          <p className="text-gray-600">
            Olá <strong>{usuario.nome_completo}</strong>, este é seu primeiro
            acesso. Por segurança, você deve criar uma nova senha.
          </p>
        </div>

        {/* Formulário */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email (apenas visualização) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
                {usuario.email}
              </div>
            </div>

            {/* Nova Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nova Senha
              </label>
              <div className="relative">
                <input
                  type={mostrarSenha ? "text" : "password"}
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  className="w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite sua nova senha"
                  required
                />
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {mostrarSenha ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirmar Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <input
                  type={mostrarConfirmar ? "text" : "password"}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirme sua nova senha"
                  required
                />
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {mostrarConfirmar ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Validações da Senha */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Requisitos da senha:
              </h4>
              <div className="space-y-2">
                <ValidacaoItem
                  valida={validacoes.minimo8}
                  texto="Mínimo 8 caracteres"
                />
                <ValidacaoItem
                  valida={validacoes.temMaiuscula}
                  texto="Pelo menos 1 letra maiúscula"
                />
                <ValidacaoItem
                  valida={validacoes.temMinuscula}
                  texto="Pelo menos 1 letra minúscula"
                />
                <ValidacaoItem
                  valida={validacoes.temNumero}
                  texto="Pelo menos 1 número"
                />
                <ValidacaoItem
                  valida={validacoes.temEspecial}
                  texto="Pelo menos 1 caractere especial"
                />
                <ValidacaoItem
                  valida={validacoes.senhasIguais}
                  texto="Senhas devem ser iguais"
                />
              </div>
            </div>

            {/* Erro */}
            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <span className="text-sm text-red-700">{erro}</span>
              </div>
            )}

            {/* Botão de Submissão */}
            <button
              type="submit"
              disabled={!senhaValida || loading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                senhaValida && !loading
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Atualizando Senha...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Criar Nova Senha
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            Após criar sua nova senha, você terá acesso completo ao sistema CIPA
            FÁCIL EHS
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Desenvolvido por <strong>Equilibrium Tecnologia</strong> -{" "}
            <a
              href="https://www.equilibriumtecnologia.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              www.equilibriumtecnologia.com.br
            </a>
          </p>
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} Equilibrium Tecnologia. Todos os
            direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetSenhaPrimeiroAcesso;
